import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Field } from '@navozyme/uikit/dist/atoms/Field';
import { DatePickerTimeInput } from '@navozyme/uikit/dist/molecules/DatePicker';
import { Form, Formik } from 'formik';

import { AppDispatch } from 'app/store';

import {
  PortFacility,
  searchPortFacilities,
} from '../../../../forms/components/search-ports-dropdown/slices/searchPortFacilitiesSlice';
import { searchPortByUnlocode } from '../../../../port-clearance-form-request/slices/portsSlice';
import {
  getPortCallData,
  PortData,
  selectPortCallData,
  selectSolas,
  updateIsps,
} from '../../../slices/portCallSlice';
import { IspsPortType } from '../../health/slices/healthSlice';

import {
  getPortFacilities,
  portCallInitialValues,
  portCallsTableColumns,
  portCallsValidationSchema,
  portSecurityLevelOptions,
} from './SolasPortCallsUtils';
import Input from '../../../../forms/components/Input';
import MultiSelect from '../../../../forms/components/MultiSelect';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import SearchPortsDropdown from '../../../../forms/components/search-ports-dropdown/components/SearchPortsDropdown';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import { cleanEmptyData } from '../../../../global/utils/cleanEmptyData';
import { countryListAllIsoData } from '../../../../global/utils/countriesList';
import { validateSchema } from '../../../../global/utils/validateForms';
import Button from '../../../../layout/components/Button';
import ConfirmationModal from '../../../../layout/components/ConfirmationModal';
import Modal from '../../../../layout/components/Modal';
import PageHeader from '../../../../layout/components/PageHeader';
import ConfirmDeleteModal from '../../../../modals/components/ConfirmDeleteModal';
import Table from '../../../../tables/components/Table';
import {
  isEmptyObject,
  MandatoryFieldMessages,
} from '../../../validations/FieldsValidation';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';

import plusIcon from '../../../../tables/styles/images/plusIcon.svg';

import '../../../../captains-form/styles/mandatory-check.scss';
import '../styles/solas-port-calls.scss';

interface ArrivalDate {
  arrivalDate: Date | null;
}

type IspsPortTypeValues = Omit<
  IspsPortType,
  'arrivalDate' | 'departureDate'
> & {
  arrivalDate: string;
  departureDate: string;
};

const SolasPortCalls = () => {
  const formRef = useRef(null);
  const { formContextRef } = useContext(FormRefContext);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = useParams();
  const solas = useSelector(selectSolas);
  const portCall = useSelector(selectPortCallData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortedLast10PortCalls, setSortedLast10PortCalls] = useState<
    IspsPortType[]
  >([]);
  const [
    sortedLast10PortCallsWithFacilityNames,
    setSortedLast10PortCallsWithFacilityNames,
  ] = useState<IspsPortType[]>([]);
  const [initialValues, setInitialValues] = useState<
    IspsPortType & { newPortFacilityName?: string }
  >({
    ...portCallInitialValues,
  });
  const [arrivalDate, setArrivalDate] = useState(
    initialValues?.arrivalDate ? new Date(initialValues?.arrivalDate) : null
  );
  const [departureDate, setDepartureDate] = useState(
    initialValues?.departureDate ? new Date(initialValues?.departureDate) : null
  );
  const [saveEnabled, setSaveEnabled] = useState(false);

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [formContextRef, formRef, solas, portCall]);

  useEffect(() => {
    if (!portCall?.data?.eta?.estimatedTime && params.uid) {
      dispatch(getPortCallData(params.uid));
    }
  }, [dispatch, params.uid, portCall?.data?.eta?.estimatedTime]);

  const handleSubmit = async (values: IspsPortType | null) => {
    if (!values) return;
    const finalValues: IspsPortTypeValues = {
      ...values,
      arrivalDate: values.arrivalDate ? values.arrivalDate.toISOString() : '',
      departureDate: values.departureDate
        ? values.departureDate.toISOString()
        : '',
    };

    values?.portNameDropdown
      ? (() => {
          finalValues.portName = values.portNameDropdown.name;
          finalValues.portUid = values.portNameDropdown.uid ?? '';
        })()
      : (() => {
          finalValues.portName = '';
          finalValues.portUid = '';
        })();

    if (finalValues?.portNameDropdown) delete finalValues.portNameDropdown;

    cleanEmptyData(finalValues);
    if (isEditingRow) {
      handleSubmitEdition(finalValues);
    } else {
      handleSubmitCreation(finalValues);
    }
    setArrivalDate(null);
    setDepartureDate(null);
    setShowPortList(false);
    setPortList([]);
  };

  const handleSubmitCreation = (finalValues: IspsPortTypeValues) => {
    const portCalls = [...sortedLast10PortCallsWithFacilityNames];

    const portCallToAdd: IspsPortType = {
      ...finalValues,
      arrivalDate: finalValues.arrivalDate
        ? new Date(finalValues.arrivalDate)
        : null,
      departureDate: finalValues.departureDate
        ? new Date(finalValues.departureDate)
        : null,
    };

    portCalls.push(portCallToAdd);

    params.uid &&
      dispatch(
        updateIsps({
          ispsData: {
            last10PortCalls: portCalls,
          },
          uid: params.uid,
        })
      );
    setIsModalOpen(false);
    setInitialValues({ ...portCallInitialValues });
  };

  const handleSubmitEdition = (finalValues: IspsPortTypeValues) => {
    const updatedLast10PortCalls = sortedLast10PortCallsWithFacilityNames.map(
      (portCall, index) => {
        return index === editingRow ? finalValues : portCall;
      }
    );

    params.uid &&
      dispatch(
        updateIsps({
          ispsData: {
            last10PortCalls: updatedLast10PortCalls,
          },
          uid: params.uid,
        })
      );

    setIsModalOpen(false);
    setEditingRow(null);
    setInitialValues({ ...portCallInitialValues });
  };

  const portFacilityDropdownId = 'solasPortFacilityDropdownId';
  const [portFacilityOptions, setPortFacilityOptions] = useState<
    PortFacility[]
  >([]);

  const [selectedPortFacility, setSelectedPortFacility] = useState('');
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const isEditingRow = editingRow !== null;

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<IspsPortType | null>(null);

  const [showPortList, setShowPortList] = useState(false);
  const [portList, setPortList] = useState<PortData[]>([]);

  const sortIspPortCallsByDate = (a: ArrivalDate, b: ArrivalDate) => {
    const dateA = a.arrivalDate ? new Date(a.arrivalDate) : null;
    const dateB = b.arrivalDate ? new Date(b.arrivalDate) : null;

    if (dateA === null && dateB === null) return 0;
    if (dateA === null) return 1; // Place null/undefined dates at the end
    if (dateB === null) return -1; // Place null/undefined dates at the end

    return dateB.getTime() - dateA.getTime();
  };

  const handleUnlocode = async (unlocode: string) => {
    const port = { countryCode: '', name: '', unlocode: '' };

    if (unlocode?.length === 5) {
      try {
        const fetchedPort = await searchPortByUnlocode({ unlocode });
        const obtainedPortFacilities = await getPortFacilities({
          portFacilities: [],
          portUnlocode: unlocode,
        });
        const updatedPortFacilityOptions = [
          ...obtainedPortFacilities.map((facility: PortFacility) => ({
            label: facility.facilityName,
            value: facility.imoPortFacilityNumber,
          })),
          { label: 'Other', value: 'Other' },
        ];
        setPortFacilityOptions(updatedPortFacilityOptions);
        setShowPortList(false);
        return fetchedPort[0] || port;
      } catch (error) {
        console.error('Error fetching port by unlocode: ', error);
        return port;
      }
    }
    return port;
  };

  const handleCountryCode = async (countryCode: string) => {
    const portList = await searchPortByUnlocode({ unlocode: countryCode });
    portList.length > 0 ? setShowPortList(true) : setShowPortList(false);
    setPortList(
      portList.map((el) => ({
        ...el,
        label: `${el.name}, ${el.countryCode}`,
        value: el?.code,
      })) || []
    );
  };

  const handlePortName = async (unlocode: string) => {
    const obtainedPortFacilities = await getPortFacilities({
      portFacilities: [],
      portUnlocode: unlocode,
    });
    const updatedPortFacilityOptions = [
      ...obtainedPortFacilities.map((facility: PortFacility) => ({
        label: facility.facilityName,
        value: facility.imoPortFacilityNumber,
      })),
      { label: 'Other', value: 'Other' },
    ];
    setPortFacilityOptions(updatedPortFacilityOptions);
  };

  useEffect(() => {
    const row = isEditingRow
      ? sortedLast10PortCallsWithFacilityNames[editingRow]
      : null;
    const portNameDropdown = {
      countryCode: row?.portCountry ? row.portCountry : '',
      name: row?.portName ? row.portName : '',
      unlocode: row?.portUnLoCode ? row.portUnLoCode : '',
    };
    setInitialValues({
      arrivalDate: row?.arrivalDate ? new Date(row.arrivalDate) : null,
      departureDate: row?.departureDate ? new Date(row.departureDate) : null,
      portNameDropdown: row?.portNameDropdown
        ? row.portNameDropdown
        : portNameDropdown.name
          ? portNameDropdown
          : null,
      portCountry: row?.portCountry ? row.portCountry : '',
      portUnLoCode: row?.portUnLoCode ? row.portUnLoCode : '',
      portName: row?.portName ? row.portName : '',
      portUid: row?.portUid ? row.portUid : '',
      portcallUid: row?.portcallUid ? row.portcallUid : '',
      portFacilityImoNumber: row?.portFacilityImoNumber
        ? row.portFacilityImoNumber
        : '',
      portFacilityName: row?.portFacilityName ? row.portFacilityName : '',
      portFacilityGisisCode: row?.portFacilityGisisCode
        ? row.portFacilityGisisCode
        : '',
      portSecurityLevel: row?.portSecurityLevel ? row.portSecurityLevel : '',
    });
    handleUnlocode(row?.portUnLoCode ? row.portUnLoCode : '');
    setArrivalDate(row?.arrivalDate ? new Date(row.arrivalDate) : null);
    setDepartureDate(row?.departureDate ? new Date(row.departureDate) : null);
  }, [editingRow, isEditingRow, sortedLast10PortCalls]);

  useEffect(() => {
    if (!initialValues.portUnLoCode) return;

    dispatch(
      searchPortFacilities({
        dropdownID: portFacilityDropdownId,
        portUnlocode: initialValues.portUnLoCode,
      })
    );
  }, [dispatch, initialValues.portUnLoCode]);

  useEffect(() => {
    if (!solas.data.last10PortCalls) return;

    setSortedLast10PortCalls(
      [...solas.data.last10PortCalls].sort((a, b) => {
        return (
          new Date(b.arrivalDate ?? '').getDate() -
          new Date(a.arrivalDate ?? '').getDate()
        );
      })
    );
  }, [solas.data.last10PortCalls]);

  useEffect(() => {
    const fetchData = async () => {
      const portCalls = [...sortedLast10PortCalls];
      for (const portCallIndex in sortedLast10PortCalls) {
        const portCall = { ...sortedLast10PortCalls[portCallIndex] };
        portCalls[portCallIndex] = portCall;
      }
      // sort the port calls by date
      portCalls.sort((d1, d2) => sortIspPortCallsByDate(d1, d2));
      setSortedLast10PortCallsWithFacilityNames(portCalls);
    };

    if (sortedLast10PortCalls) fetchData();
  }, [sortedLast10PortCalls]);

  useEffect(() => {
    if (
      sortedLast10PortCallsWithFacilityNames &&
      sortedLast10PortCallsWithFacilityNames.length > 0
    ) {
      setSaveEnabled(true);
    } else {
      setSaveEnabled(false);
    }
  }, [sortedLast10PortCallsWithFacilityNames]);

  const [confirmDeleteMemberModalData, setConfirmDeleteMemberModalData] =
    useState<{
      index: number | null;
      showModal: boolean;
    }>({
      index: null,
      showModal: false,
    });
  const [
    showConfirmDeleteAllMembersModal,
    setShowConfirmDeleteAllMembersModal,
  ] = useState(false);

  const onCloseModal = () => {
    setIsModalOpen(false);
    setInitialValues({ ...portCallInitialValues });
    setShowPortList(false);
    setPortList([]);
    setTimeout(() => setEditingRow(null), 300);
    setArrivalDate(null);
    setDepartureDate(null);
  };

  return (
    <div className="solas-port-calls-table">
      <PageHeader
        title={'Ship and Port Security'}
        subtitle={`Please, indicate the list of last 10 calls and port facility in chronological order (most recent call first):`}
      />
      <p>Ship pre-arrival security information form.</p>
      <Table
        useRowNumbers
        title={'Last port calls'}
        addButtonText={'Add port call'}
        data={sortedLast10PortCallsWithFacilityNames}
        columns={portCallsTableColumns}
        editingMode={isEditingTable}
        setEditingMode={setIsEditingTable}
        setIsAddModalOpen={setIsModalOpen}
        setIsDeleteModalOpen={setShowConfirmDeleteAllMembersModal}
        onEditButtonClick={(rowIndex) => {
          setEditingRow(rowIndex);
          setIsModalOpen(true);
        }}
        onDeleteButtonClick={(rowIndex) => {
          setConfirmDeleteMemberModalData({
            showModal: true,
            index: rowIndex,
          });
        }}
      />
      {sortedLast10PortCallsWithFacilityNames.length <= 0 && (
        <p className="error-table">{MandatoryFieldMessages.RequiredTable}</p>
      )}
      <Modal
        className={`add-port-call-modal`}
        title={'Add a port call'}
        showModal={isModalOpen}
        onCloseModal={onCloseModal}>
        <ConfirmationModal
          title="Have you finished editing?"
          subtitle="Some required fields have not been completed."
          disabledSubtitle="You have to complete at least one field."
          showModal={isConfirmationModalOpen}
          handleCloseModal={setIsConfirmationModalOpen}
          willDisableSave={formValues}
          onAcceptModal={() => {
            setIsConfirmationModalOpen(false);
            setIsModalOpen(false);
            handleSubmit(formValues);
          }}
          className=""
        />
        <div className="add-port-call-modal__content">
          <Formik
            innerRef={formRef}
            onSubmit={handleSubmit}
            enableReinitialize
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            validate={(values) => {
              return validateSchema(
                values,
                portCallsValidationSchema(
                  editingRow,
                  values,
                  sortedLast10PortCallsWithFacilityNames,
                  portCall?.data?.eta?.estimatedTime ?? ''
                ),
                setFormValues,
                setIsConfirmationModalOpen,
                setIsModalOpen
              );
            }}>
            {({ values, setValues, setFieldValue, errors }) => {
              const isFormInvalid = () => {
                const isPortFacilityOther =
                  values.portFacilityImoNumber === 'Other';
                return (
                  !values.arrivalDate ||
                  !values.departureDate ||
                  !values.portNameDropdown ||
                  !values.portCountry ||
                  !values.portUnLoCode ||
                  !values.portSecurityLevel ||
                  (!isPortFacilityOther && !values.portFacilityGisisCode) ||
                  !values.portFacilityImoNumber ||
                  (isPortFacilityOther && !values.newPortFacilityName)
                );
              };
              return (
                <Form className="add-port-call-form">
                  <div className="add-port-call-form__row">
                    <Field label="Date From" className="field_date_picker">
                      <DatePickerTimeInput
                        key={initialValues?.arrivalDate + 'arrivalDate'}
                        name="arrivalDate"
                        sizeInput="large"
                        maxDate={
                          portCall?.data?.eta?.estimatedTime
                            ? new Date(portCall?.data?.eta?.estimatedTime)
                            : new Date()
                        }
                        showTime={true}
                        defaultValue={arrivalDate}
                        action={(selected: {
                          inputDate: string;
                          inputTime: string;
                        }) => {
                          const isoDate = `${selected.inputDate}T${selected.inputTime}`;
                          setArrivalDate(new Date(isoDate));
                          setFieldValue(
                            'arrivalDate',
                            new Date(isoDate + ':00Z')
                          );
                        }}
                        hasError={errors?.arrivalDate}
                        errorMessage={errors?.arrivalDate}
                        required
                        popperPlacement="right"
                      />
                    </Field>
                    <Field label="Date To" className="field_date_picker">
                      <DatePickerTimeInput
                        key={initialValues?.departureDate + 'departureDate'}
                        name="departureDate"
                        sizeInput="large"
                        minDate={
                          arrivalDate ? arrivalDate : initialValues?.arrivalDate
                        }
                        maxDate={
                          portCall?.data?.eta?.estimatedTime
                            ? new Date(portCall?.data?.eta?.estimatedTime)
                            : new Date()
                        }
                        showTime={true}
                        defaultValue={departureDate}
                        action={(selected: {
                          inputDate: string;
                          inputTime: string;
                        }) => {
                          const isoDate = `${selected.inputDate}T${selected.inputTime}`;
                          setDepartureDate(new Date(isoDate));
                          setFieldValue(
                            'departureDate',
                            new Date(isoDate + ':00Z')
                          );
                        }}
                        hasError={errors?.departureDate}
                        errorMessage={errors?.departureDate}
                        required
                        popperPlacement="right"
                      />
                    </Field>
                  </div>
                  <div className="add-port-call-form__row">
                    <Input
                      name="portUnLoCode"
                      label="UN/LOCODE"
                      onChange={async (e: { target: { value: string } }) => {
                        const inputCode = e.target.value.toUpperCase();
                        setValues({
                          ...values,
                          portUnLoCode: inputCode,
                        });
                        const port = await handleUnlocode(inputCode);
                        setValues({
                          ...values,
                          portUnLoCode: inputCode,
                          portCountry: port.countryCode,
                          portNameDropdown: port,
                          portName: port.name,
                        });
                      }}
                    />
                    <MultiSelect
                      name="portCountry"
                      options={countryListAllIsoData}
                      isMulti={false}
                      label="Country"
                      onChange={async (selectedOption: {
                        value: string;
                        label: string;
                      }) => {
                        setValues({
                          ...values,
                          portCountry: selectedOption.value,
                          portUnLoCode: '',
                          portName: '',
                          portNameDropdown: null,
                        });
                        await handleCountryCode(selectedOption.value);
                      }}
                    />
                    {showPortList ? (
                      <MultiSelect
                        name="portNameDropdown"
                        options={portList}
                        isMulti={false}
                        label="Port"
                        placeholder=""
                        onChange={async (selectedOption: PortData) => {
                          setInitialValues({
                            ...values,
                            portUnLoCode: selectedOption.unlocode,
                            portCountry: selectedOption.countryCode,
                            portNameDropdown: selectedOption,
                            portName: selectedOption.name,
                          });
                          await handlePortName(selectedOption.unlocode);
                        }}
                        required
                      />
                    ) : (
                      <SearchPortsDropdown
                        label="Port"
                        name="portNameDropdown"
                        onChange={async (port: PortData) => {
                          setInitialValues({
                            ...values,
                            portNameDropdown: port,
                            portCountry: port.countryCode,
                            portUnLoCode: port.unlocode,
                          });
                          await handlePortName(port.unlocode);
                        }}
                        dropdownID={values.portName}
                        placeholder="Type One"
                        required
                      />
                    )}
                  </div>
                  <div className="add-port-call-form__row">
                    <MultiSelect
                      label="Port Facility"
                      name="portFacilityImoNumber"
                      options={portFacilityOptions}
                      disabled={
                        isEmptyObject(values.portNameDropdown) &&
                        isEmptyObject(initialValues.portNameDropdown)
                      }
                      isMulti={false}
                      onChange={(selectedOption: {
                        value: string;
                        label: string;
                      }) => {
                        setSelectedPortFacility(selectedOption.value);
                        setValues({
                          ...values,
                          portFacilityImoNumber: selectedOption.value,
                          portFacilityGisisCode:
                            selectedOption.value === 'Other' ||
                            selectedOption.label === 'Other'
                              ? ''
                              : selectedOption.value,
                          portFacilityName: selectedOption.label,
                        });
                      }}
                      required
                    />
                    {selectedPortFacility === 'Other' && (
                      <Input
                        name="newPortFacilityName"
                        label="New port facility name"
                        placeholder="Enter the new facility name"
                        disabled={
                          isEmptyObject(values.portNameDropdown) &&
                          isEmptyObject(initialValues.portNameDropdown)
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setValues({
                            ...values,
                            portFacilityName: e.target.value,
                            newPortFacilityName: e.target.value,
                          });
                        }}
                        maxLength={25}
                        required
                      />
                    )}
                    <Input
                      name="portFacilityGisisCode"
                      label="GISIS Code"
                      disabled={
                        isEmptyObject(values.portNameDropdown) &&
                        isEmptyObject(initialValues.portNameDropdown)
                      }
                      maxLength={10}
                      required={selectedPortFacility !== 'Other'}
                    />
                  </div>
                  <div className="add-port-call-form__row">
                    <RadioButtonsCustom
                      name="portSecurityLevel"
                      label="Ship Security Level"
                      options={portSecurityLevelOptions}
                    />
                  </div>
                  {isEditingRow ? (
                    <div className="add-member__form-submission-btns">
                      <div className="discard-btn" onClick={onCloseModal}>
                        Discard
                      </div>
                      <Button type="submit">Change Information</Button>
                    </div>
                  ) : (
                    <div className="add-member__form-submission-btns">
                      <Button
                        iconLeft={plusIcon}
                        type="submit"
                        disabled={isFormInvalid()}>
                        Add
                      </Button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>
      </Modal>
      <ConfirmDeleteModal
        title={
          <h3>
            Are you sure you want to{' '}
            <span>
              delete all the <br />
              crew members
            </span>{' '}
            from the list?
          </h3>
        }
        show={showConfirmDeleteAllMembersModal}
        onCancelClick={() => setShowConfirmDeleteAllMembersModal(false)}
        onDeleteClick={() => {
          setShowConfirmDeleteAllMembersModal(false);

          params.uid &&
            dispatch(
              updateIsps({
                ispsData: {
                  last10PortCalls: [],
                },
                uid: params.uid,
              })
            );
        }}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <ConfirmDeleteModal
        title={<h3>Are you sure you want to delete this crew member?</h3>}
        show={confirmDeleteMemberModalData.showModal}
        onCancelClick={() => {
          setConfirmDeleteMemberModalData({
            index: null,
            showModal: false,
          });
        }}
        onDeleteClick={() => {
          const last10PortCalls = sortedLast10PortCalls.filter(
            (_, index) => index !== confirmDeleteMemberModalData.index
          );

          params.uid &&
            dispatch(
              updateIsps({
                ispsData: {
                  last10PortCalls,
                },
                uid: params.uid,
              })
            );

          setConfirmDeleteMemberModalData({
            index: null,
            showModal: false,
          });
        }}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <SkipAndNextButtons
        onSubmitClick={() =>
          navigate(`/${params.uid}/solas-and-isps-code/security-measures`)
        }
        next={`/${params.uid}/solas-and-isps-code/security-measures`}
        back={`/${params.uid}/solas-and-isps-code/questions`}
        submitDisabled={!saveEnabled}
      />
    </div>
  );
};

export default SolasPortCalls;
